import {
  SHAPEFILE_DETAILS_LABEL_LAYER,
  SHAPEFILE_NAME_LABELS_LAYER
} from "constants/mapLayers.constants";
import { useMapStore } from "stores/map";
import type { MapLayer } from "stores/map";

import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

interface IUpdateLayerZoomParams {
  mapLayers: readonly MapLayer[];
  layerNames: string[];
  minAndMax: [number, number];
}

export function useUpdateLayerZoom() {
  const setDisplaySettings = useMapStore((s) => s.setDisplaySettings);
  const setLayers = useMapStore((s) => s.setLayers);

  const updateLayerZoom = ({
    mapLayers,
    layerNames,
    minAndMax
  }: IUpdateLayerZoomParams) => {
    // Functional updater (EVA-63) — computes from the current store value
    // rather than a stale render-time `displaySettings` snapshot, avoiding a
    // lost update against a concurrently-applied View restore.
    setDisplaySettings((current) => {
      const updatedZoomSettings = { ...current.layerZoomSettings };
      layerNames.forEach((layerId) => {
        updatedZoomSettings[layerId] = {
          minzoom: minAndMax[0],
          maxzoom: minAndMax[1]
        };
      });

      return {
        ...current,
        layerZoomSettings: updatedZoomSettings
      };
    });

    const shouldUpdateShapefileLabels = layerNames.includes(SHAPEFILE_NAME_LABELS_LAYER);

    const shouldUpdateShapefileDetailsLabel = layerNames.includes(
      SHAPEFILE_DETAILS_LABEL_LAYER
    );

    const updatedMapLayers = mapLayers.map((layer) => {
      const isDirectMatch = layerNames.includes(layer.id);
      const isShapefileLabelLayer =
        shouldUpdateShapefileLabels && layer.id.includes(shapefileLayerType.label);
      const isShapefileDetailsLabelLayer =
        shouldUpdateShapefileDetailsLabel &&
        layer.id.includes(shapefileLayerType.property);

      if (isDirectMatch || isShapefileLabelLayer || isShapefileDetailsLabelLayer) {
        return {
          ...layer,
          minzoom: minAndMax[0],
          maxzoom: minAndMax[1]
        };
      }
      return { ...layer };
    });

    setLayers(updatedMapLayers);
  };

  return { updateLayerZoom };
}
